import { Autocomplete as MuiAutocomplete, useTheme } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, SyntheticEvent } from "react";
import { COUNTRIES, CountryOption } from "./country-options";
import { CountrySelectProps } from "./CountrySelect.props";
import { CountryTextField, makePaperStyles } from "./CountrySelect.styles";

const getOptionLabel = ({ label }: CountryOption) => label;

/**
 * Looks a value up as a country code and, to support values stored before the
 * country became a select, as a country name.
 */
const findCountry = (value: string) => {
  const searched = value.trim().toLowerCase();

  return (
    COUNTRIES.find(({ code }) => code.toLowerCase() === searched) ??
    COUNTRIES.find(({ label }) => label.toLowerCase() === searched)
  );
};

const isOptionEqualToValue = (option: CountryOption, selected: CountryOption) =>
  option.code === selected.code;

/**
 * Form validator for the value of a CountrySelect. Legacy values are shown by
 * the input but rejected here, so they have to be replaced with a country of
 * the list before the form can be submitted.
 *
 * Emptiness is not validated, combine with `required` when the field is
 * mandatory.
 *
 * @returns key of the error message, or undefined when the value is valid
 */
export const validCountry = (value?: string) =>
  !value || COUNTRIES.some(({ code }) => code === value) ? undefined : "noValidOptionSelectedError";

const CountrySelectComponent = ({
  value = "",
  onChange,
  width = "100%",
  disabled,
  ...inputProps
}: CountrySelectProps) => {
  const theme = useTheme();

  /**
   * Values stored before the country became a select are plain country names.
   * A name that is known keeps its country selected, an unknown one is kept as
   * its own option to be shown as-is until the user picks one from the list.
   */
  const selectedOption = useMemo<CountryOption | null>(() => {
    if (!value) {
      return null;
    }

    return findCountry(value) ?? { code: value, label: value };
  }, [value]);

  /**
   * Report the code of a country that was selected by its name back to the
   * form, so that the name is replaced by the code on save.
   */
  useEffect(() => {
    if (selectedOption && selectedOption.code !== value) {
      onChange(selectedOption.code);
    }
  }, [selectedOption, value, onChange]);

  const options = useMemo(
    () =>
      selectedOption && !COUNTRIES.includes(selectedOption)
        ? [selectedOption, ...COUNTRIES]
        : COUNTRIES,
    [selectedOption]
  );

  const handleChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, option: CountryOption | null) => {
      onChange(option?.code ?? "");
    },
    [onChange]
  );

  return (
    <MuiAutocomplete
      options={options}
      value={selectedOption}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      disabled={disabled}
      autoHighlight
      sx={{ width }}
      renderInput={(params) => <CountryTextField {...params} {...inputProps} />}
      slotProps={{
        paper: {
          sx: makePaperStyles(theme)
        }
      }}
    />
  );
};

export const CountrySelect = memo(CountrySelectComponent);
